import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ChatAttachment } from '@volt/shared';
import type { ApiClient } from '../api/client.js';
import { Icon } from './Icon.js';
import { ImageLightbox } from './MediaLightbox.js';

const URL_TTL_MS = 10 * 60_000;
const urlCache = new Map<string, { url: string; expiresAt: number }>();
const pending = new Map<string, Promise<string>>();

/**
 * Signed link for an attachment. Inline image bubbles ask for the `preview` variant (a
 * small WebP the server keeps next to large images); full-screen views and downloads
 * use the untouched original.
 */
function loadUrl(
  api: ApiClient,
  id: string,
  variant: 'original' | 'preview' = 'original',
): Promise<string> {
  const key = `${id}:${variant}`;
  const cached = urlCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.url);
  const running = pending.get(key);
  if (running) return running;
  const request = api
    .getDirectChatAttachmentUrl(id, variant)
    .then(({ url }) => {
      urlCache.set(key, { url, expiresAt: Date.now() + URL_TTL_MS });
      return url;
    })
    .finally(() => pending.delete(key));
  pending.set(key, request);
  return request;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ImageAttachment({ api, attachment }: { api: ApiClient; attachment: ChatAttachment }) {
  const [url, setUrl] = useState<string | null>(
    urlCache.get(`${attachment.id}:preview`)?.url ?? null,
  );
  const [original, setOriginal] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [viewing, setViewing] = useState(false);
  useEffect(() => {
    let active = true;
    loadUrl(api, attachment.id, 'preview')
      .then((next) => active && setUrl(next))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [api, attachment.id]);
  useEffect(() => {
    if (!viewing || original) return;
    let active = true;
    loadUrl(api, attachment.id, 'original')
      .then((next) => active && setOriginal(next))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api, attachment.id, viewing, original]);
  if (failed) return <FileAttachment api={api} attachment={attachment} />;
  return (
    <>
      <button
        type="button"
        className="direct-chat-attachment-image"
        aria-label={attachment.fileName}
        disabled={!url}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={() => setViewing(true)}
      >
        {url ? <img src={url} alt={attachment.fileName} loading="lazy" decoding="async" /> : null}
      </button>
      {viewing && url
        ? createPortal(
            <ImageLightbox
              urls={[original ?? url]}
              initialIndex={0}
              onClose={() => setViewing(false)}
            />,
            document.body,
          )
        : null}
    </>
  );
}

function FileAttachment({ api, attachment }: { api: ApiClient; attachment: ChatAttachment }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className="direct-chat-attachment-file"
      disabled={busy}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={() => {
        setBusy(true);
        loadUrl(api, attachment.id)
          .then((url) => window.open(url, '_blank', 'noopener'))
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
    >
      <Icon name="attach" size={18} />
      <span className="direct-chat-attachment-name">{attachment.fileName}</span>
      <small>{formatFileSize(attachment.size)}</small>
    </button>
  );
}

function VideoAttachment({ api, attachment }: { api: ApiClient; attachment: ChatAttachment }) {
  const [url, setUrl] = useState<string | null>(
    urlCache.get(`${attachment.id}:original`)?.url ?? null,
  );
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    loadUrl(api, attachment.id)
      .then((next) => active && setUrl(next))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [api, attachment.id]);
  if (failed) return <FileAttachment api={api} attachment={attachment} />;
  return (
    <video
      className="direct-chat-attachment-video"
      controls
      playsInline
      preload="metadata"
      src={url ?? undefined}
      aria-label={attachment.fileName}
      onPointerDown={(event) => event.stopPropagation()}
    />
  );
}

function AudioAttachment({ api, attachment }: { api: ApiClient; attachment: ChatAttachment }) {
  const [url, setUrl] = useState<string | null>(
    urlCache.get(`${attachment.id}:original`)?.url ?? null,
  );
  useEffect(() => {
    let active = true;
    loadUrl(api, attachment.id)
      .then((next) => active && setUrl(next))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [api, attachment.id]);
  return (
    <audio
      className="direct-chat-attachment-audio"
      controls
      preload="metadata"
      src={url ?? undefined}
      onPointerDown={(event) => event.stopPropagation()}
    />
  );
}

export function ChatAttachments({
  api,
  attachments,
}: {
  api: ApiClient;
  attachments: ChatAttachment[];
}) {
  return (
    <div className="direct-chat-attachments">
      {attachments.map((attachment) =>
        attachment.mime.startsWith('image/') ? (
          <ImageAttachment key={attachment.id} api={api} attachment={attachment} />
        ) : attachment.mime.startsWith('video/') ? (
          <VideoAttachment key={attachment.id} api={api} attachment={attachment} />
        ) : attachment.mime.startsWith('audio/') ? (
          <AudioAttachment key={attachment.id} api={api} attachment={attachment} />
        ) : (
          <FileAttachment key={attachment.id} api={api} attachment={attachment} />
        ),
      )}
    </div>
  );
}
